import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";

// A scroll container that only mounts the items inside the viewport (plus a
// small overscan), so a list of a few thousand users costs about the same to
// render as a list of twenty. Item heights are declared up front rather than
// measured, so offsets are pure arithmetic.
//
// `isSticky` marks section headers: once scrolled past, a sticky item stays
// pinned to the top of the viewport until the next one pushes it away, matching
// the `position: sticky` headers this replaces.

const DEFAULT_OVERSCAN = 4;

// jsdom, and the first render before layout has run, report a zero height. Fall
// back to a plausible viewport so the list still renders rather than collapsing
// to nothing.
const FALLBACK_VIEWPORT_HEIGHT = 400;

// Index of the last item starting at or before `offset`
function findItemAtOffset(offsets, offset) {
    let low = 0;
    let high = offsets.length - 1;
    let match = 0;

    while (low <= high) {
        const mid = (low + high) >> 1;
        if (offsets[mid] <= offset) {
            match = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return match;
}

export default function VirtualizedList({
    items,
    itemHeight,
    renderItem,
    getItemKey,
    isSticky,
    overscan = DEFAULT_OVERSCAN,
    ...boxProps
}) {
    const scrollRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(
        FALLBACK_VIEWPORT_HEIGHT,
    );

    const heightOf =
        typeof itemHeight === "function" ? itemHeight : () => itemHeight;

    const { offsets, totalHeight } = useMemo(() => {
        const itemOffsets = new Array(items.length);
        let runningHeight = 0;

        for (let index = 0; index < items.length; index += 1) {
            itemOffsets[index] = runningHeight;
            runningHeight += heightOf(items[index], index);
        }

        return { offsets: itemOffsets, totalHeight: runningHeight };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, itemHeight]);

    useEffect(() => {
        const node = scrollRef.current;
        if (!node) {
            return undefined;
        }

        const measure = () =>
            setViewportHeight(node.clientHeight || FALLBACK_VIEWPORT_HEIGHT);

        measure();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    // A search that narrows the list leaves the browser to clamp its own scroll
    // position; clamp ours the same way rather than rendering an empty window
    // until the scroll event that follows lands
    const top = Math.max(0, Math.min(scrollTop, totalHeight - viewportHeight));

    const firstVisible = items.length > 0 ? findItemAtOffset(offsets, top) : 0;
    const lastVisible =
        items.length > 0 ? findItemAtOffset(offsets, top + viewportHeight) : -1;

    const startIndex = Math.max(0, firstVisible - overscan);
    const endIndex = Math.min(items.length - 1, lastVisible + overscan);

    // The header of the section the top of the viewport is currently inside
    let pinnedIndex = -1;
    if (isSticky) {
        for (let index = firstVisible; index >= 0; index -= 1) {
            if (isSticky(items[index], index)) {
                if (offsets[index] < top) {
                    pinnedIndex = index;
                }
                break;
            }
        }
    }

    const visibleIndexes = [];
    for (let index = startIndex; index <= endIndex; index += 1) {
        if (index !== pinnedIndex) {
            visibleIndexes.push(index);
        }
    }

    const positioned = (index, style) => (
        <div
            // Offsets are a per-item value, so they go on the style attribute
            // rather than through `sx`, which would compile a class per row
            key={getItemKey(items[index], index)}
            style={{
                position: "absolute",
                left: 0,
                right: 0,
                height: heightOf(items[index], index),
                ...style,
            }}
        >
            {renderItem(items[index], index)}
        </div>
    );

    return (
        <Box
            {...boxProps}
            ref={scrollRef}
            onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        >
            <div style={{ position: "relative", height: totalHeight }}>
                {pinnedIndex !== -1 &&
                    positioned(pinnedIndex, {
                        top: 0,
                        zIndex: 2,
                        transform: `translateY(${top}px)`,
                    })}
                {visibleIndexes.map((index) =>
                    positioned(index, {
                        top: 0,
                        transform: `translateY(${offsets[index]}px)`,
                    }),
                )}
            </div>
        </Box>
    );
}
